import { buildQueryParams } from "./buildQueryParams";
import {
  getCurrentMonthRange,
  toApiDate,
  validateDateRange,
} from "./dateUtils";
import {
  downloadFileFromApi,
  handleDownloadError,
} from "./downloadFile";

const MAX_RANGE_MONTHS = 1;

type DateRangeFilter = {
  start: string;
  end: string;
};

export type ReportFilters = {
  material: string;
  description: string;
  emitter: string;
  centers: string[];
  statuses: string[];
  startDate: string;
  endDate: string;
  classificationQuizStartDate: string;
  classificationQuizFinishDate: string;
  classificationApprovalStartDate: string;
  classificationApprovalEndDate: string;
};

type QueryParamValue = string | string[] | undefined | null;

type ReportDownloadConfig<TFilters extends ReportFilters> = {
  getFilters: () => TFilters;
  hasAnyNonDateFilter: (filters: TFilters) => boolean;
  endpoint: string;
  setIsDownloading: (value: boolean) => void;
  setBackgroudError: (value: string) => void;
  getAdditionalQueryParams?: (
    filters: TFilters,
  ) => Record<string, QueryParamValue>;
  normalizeStatuses?: (statuses: string[]) => string[];
};

export type ReportDownloadResult =
  | { success: true }
  | { success: false; message: string };

export const MISSING_PERIOD_MESSAGE =
  "Por favor selecione um período de no máximo 1 mês em qualquer filtro de data para baixar o relatório.";

const isCompleteRange = ({ start, end }: DateRangeFilter): boolean =>
  !!start && !!end;

const isPartialRange = ({ start, end }: DateRangeFilter): boolean =>
  !!start !== !!end;

const validateReportRanges = (ranges: DateRangeFilter[]): string => {
  for (const range of ranges) {
    if (!isPartialRange(range) && !isCompleteRange(range)) {
      continue;
    }

    const rangeError = validateDateRange(
      range.start,
      range.end,
      MAX_RANGE_MONTHS,
    );

    if (rangeError) {
      return rangeError;
    }
  }

  return "";
};

export const downloadReport = async <TFilters extends ReportFilters>(
  config: ReportDownloadConfig<TFilters>,
): Promise<ReportDownloadResult> => {
  const filters = config.getFilters();
  const entryRange = { start: filters.startDate, end: filters.endDate };
  const quizRange = {
    start: filters.classificationQuizStartDate,
    end: filters.classificationQuizFinishDate,
  };
  const approvalRange = {
    start: filters.classificationApprovalStartDate,
    end: filters.classificationApprovalEndDate,
  };
  const dateRanges = [entryRange, quizRange, approvalRange];
  const hasDates = dateRanges.some(isCompleteRange);
  const rangeError = validateReportRanges(dateRanges);

  if (rangeError) {
    config.setBackgroudError("#FFD9D9");

    return { success: false, message: rangeError };
  }

  if (!hasDates && config.hasAnyNonDateFilter(filters)) {
    config.setBackgroudError("#FFDF95");

    return { success: false, message: MISSING_PERIOD_MESSAGE };
  }

  const fileNameRange = dateRanges.find(isCompleteRange);
  const { startDate, endDate } = fileNameRange
    ? {
        startDate: toApiDate(fileNameRange.start),
        endDate: toApiDate(fileNameRange.end),
      }
    : getCurrentMonthRange();

  const hasEntryRange = isCompleteRange(entryRange);
  let queryStartDate = startDate;
  let queryEndDate = endDate;

  if (hasEntryRange) {
    queryStartDate = toApiDate(filters.startDate);
    queryEndDate = toApiDate(filters.endDate);
  } else if (hasDates) {
    queryStartDate = "";
    queryEndDate = "";
  }
  const additionalQueryParams =
    config.getAdditionalQueryParams?.(filters) ?? {};
  const statuses =
    config.normalizeStatuses?.(filters.statuses) ?? filters.statuses;

  const query = buildQueryParams({
    material: filters.material,
    description: filters.description,
    emitter: filters.emitter,
    centers: filters.centers,
    statuses,
    ...additionalQueryParams,
    startDate: queryStartDate,
    endDate: queryEndDate,
    classificationQuizStartDate: toApiDate(
      filters.classificationQuizStartDate,
    ),
    classificationQuizFinishDate: toApiDate(
      filters.classificationQuizFinishDate,
    ),
    classificationApprovalStartDate: toApiDate(
      filters.classificationApprovalStartDate,
    ),
    classificationApprovalEndDate: toApiDate(
      filters.classificationApprovalEndDate,
    ),
  });

  try {
    config.setIsDownloading(true);

    await downloadFileFromApi(
      `${config.endpoint}?${query}`,
      `Relatório_${startDate}_${endDate}.xlsx`,
    );

    return { success: true };
  } catch (error: any) {
    const message = await handleDownloadError(
      error,
      config.setBackgroudError,
    );

    return {
      success: false,
      message,
    };
  } finally {
    config.setIsDownloading(false);
  }
};